<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useCondition } from "./useCondition";

import InputDropdown from "@core/input/InputDropdown.vue";

import { useTargetStore } from "../target/targets.store";
import { useResourceStore } from "../resource/resources.store";
import { useIndexStore } from "../index/indices.store";
import { useActionStore } from "../action/actions.store";
import { useIncidentStore } from "../incident/incidents.store";
import { useFeatures } from "@editor/store/features.store";
import { useSettingsStore } from "@editor/store/settings.store";

import type { ConditionActivatorType, ConditionItem } from "./ConditionItem";

interface Props {
  conditionId: number;
}
const props = defineProps<Props>();

const { t } = useI18n();
const ctx = useCondition(() => props.conditionId);

const features = useFeatures();
const settings = useSettingsStore();
const targetStore = useTargetStore();
const resourceStore = useResourceStore();
const indexStore = useIndexStore();
const actionStore = useActionStore();
const incidentStore = useIncidentStore();

const activatorTypeOptions = computed(() => {
  const types: ConditionActivatorType[] = ["round", "roundRange"];
  if (features.target) types.push("target");
  if (features.resource) types.push("resource");
  if (features.index) types.push("index");
  if (features.incident) types.push("incident");
  if (features.action) types.push("action");

  return types.map((type) => {
    return {
      value: type,
      label: t(`activatorTypes.${type}`)
    };
  });
});
const activeActivatorTypeOption = computed(() => {
  return activatorTypeOptions.value.find(
    (option) => option.value == ctx.condition.activatorType
  );
});

function setActivatorType(type: ConditionItem["activatorType"]) {
  const changes: Partial<ConditionItem> = {
    activatorType: type
  };

  if (type == ctx.condition.activatorType) return;

  // always reset the id when switching type:
  changes.activatorId = null;

  if (
    ctx.condition.activatorProperty &&
    !getActivatorProperties(type).includes(ctx.condition.activatorProperty)
  ) {
    changes.activatorProperty = null;
  }

  if (type == "roundRange") {
    changes.activatorEndType = "absoluteRound";
    changes.activatorEnd = Math.max(0, settings.simulationRoundAmount - 1);
    changes.operator = "?";
  } else if (type == "round") {
    changes.comparedValueType = "absoluteRound";
    changes.comparedProperty = "activationTime";
  }

  ctx.validUpdate(changes);
}

function getActivatorProperties(
  type: ConditionItem["activatorType"]
): ConditionItem["activatorProperty"][] {
  switch (type) {
    case "target":
    case "resource":
    case "index":
      return ["value", "increase", "decrease"]; // "valueChange"
    case "incident":
    case "action":
      return ["activationCount", "activationTime"];
    default:
      return [];
  }
}

const activatorProperties = computed(() =>
  getActivatorProperties(ctx.condition.activatorType)
);
const activatorPropertyOptions = computed(() => {
  return activatorProperties.value.map((property) => {
    return {
      value: property,
      label: t(`activatorPropertyLabels.${ctx.condition.activatorType}.${property}`)
    };
  });
});
const activeActivatorPropertyOption = computed(() => {
  return activatorPropertyOptions.value.find(
    (option) => option.value == ctx.condition.activatorProperty
  );
});

function updateActivatorProperty(property: ConditionItem["activatorProperty"]) {
  const changes: Partial<ConditionItem> = {
    activatorProperty: property
  };

  const condition = ctx.condition;

  if (property == condition.activatorProperty) return;

  if (condition.activatorType != "round") {
    changes.comparedValueType = "number";
  }

  if (
    property == "activationCount" ||
    property == "valueChange" ||
    property == "increase" ||
    property == "decrease"
  ) {
    if (condition.activatorStartType == null) {
      changes.activatorStartType = "startRound";
      changes.activatorStart = null;
    }
    if (condition.activatorEndType == null) {
      changes.activatorEndType = "currentRound";
      changes.activatorEnd = null;
    }
  } else if (property == "activationTime") {
    changes.comparedValueType = "absoluteRound";
    changes.comparedProperty = "activationTime";
  }

  console.log(changes);

  ctx.validUpdate(changes);
}

const activatorIdRequired = computed(() => {
  return (
    ctx.condition.activatorType != "round" &&
    ctx.condition.activatorType != "roundRange"
  );
});

const activatorItems = computed(() => {
  switch (ctx.condition.activatorType) {
    case "target":
      return targetStore.items;
    case "resource":
      return resourceStore.items;
    case "index":
      return indexStore.items;
    case "incident":
      return incidentStore.items;
    case "action":
      return actionStore.items;
    default:
      return [];
  }
});
const activatorItemsOrder = computed(() => {
  switch (ctx.condition.activatorType) {
    case "target":
      return targetStore.order;
    case "resource":
      return resourceStore.order;
    case "index":
      return indexStore.order;
    case "incident":
      return incidentStore.order;
    case "action":
      return actionStore.order;
    default:
      return [];
  }
});

const variants = computed(() => {
  switch (ctx.condition.activatorProperty) {
    case "valueChange":
      return ["positive", "negative"];
    default:
      return [];
  }
});
</script>

<template>
  <div class="condition-input-activator column">
    <input-dropdown
      :label="$t('labels.type')"
      :value="activeActivatorTypeOption"
      :options="activatorTypeOptions"
      prop="label"
      item-key="value"
      :searchable="true"
      @update:value="setActivatorType($event.value)"
    ></input-dropdown>

    <input-dropdown
      v-if="ctx.condition.activatorType && activatorProperties.length > 0"
      :label="$t('labels.property')"
      :options="activatorPropertyOptions"
      :value="activeActivatorPropertyOption"
      :searchable="true"
      prop="label"
      item-key="value"
      @update:value="updateActivatorProperty($event.value)"
    ></input-dropdown>

    <!-- ONLY USED IN DEACTIVATED PROPERTY "valueChange" -->
    <input-dropdown
      v-if="variants.length > 0"
      v-slot="{ item: propertyVariant }"
      :value="ctx.condition.activatorPropertyVariant"
      :options="variants"
      :label="$t('labels.propertyVariant')"
      :searchable="true"
      @update:value="ctx.validUpdateKey('activatorPropertyVariant', $event)"
    >
      {{ $t(`activatorPropertyVariants.${propertyVariant}`) }}
    </input-dropdown>

    <input-dropdown
      v-if="
        ctx.condition.activatorType &&
        ctx.condition.activatorProperty &&
        activatorIdRequired
      "
      v-slot="{ item }"
      :label="$t(`activatorTypes.${ctx.condition.activatorType}`)"
      :value="ctx.activatorItem"
      :options="activatorItems"
      :order="activatorItemsOrder"
      :searchable="true"
      @update:value="ctx.validUpdateKey('activatorId', $event.id)"
    >
      {{ activatorItems[item.id].title }}
    </input-dropdown>
  </div>
</template>

<style lang="scss">
.condition-input-activator {
  // background-color: rgba(var(--tint-contrast), 0.01);
  // background-color: var(--color-background);
  // border: 1px solid var(--color-border);

  margin-top: -0.5rem;
  padding: 0.5rem 0.75rem;
  border-left: 2px solid var(--color-border);
  box-shadow: 1px 0 0.25rem var(--color-shadow-1);
}
</style>
